import { useState, useEffect, useMemo } from "react";
import type { BulkAction, TableState } from "./types";
import { getHandler } from "./actions";
import SelectStatic from "../select/SelectStatic";
import SelectLookup from "../select/SelectLookup";
import type { SelectOption } from "../select/Select.types";

interface Props {
  actions: BulkAction[];
  selectedRows: Record<string, any>[];
  selectedCount: number;
  totalCount?: number;
  selectAcross: boolean;
  tableState?: TableState;
  onSelectAll: () => void;
  onClearSelection: () => void;
}

export default function BulkActions({ actions, selectedRows, selectedCount, totalCount, selectAcross, tableState, onSelectAll, onClearSelection }: Props) {
  const [selectedAction, setSelectedAction] = useState("");
  const [optionValues, setOptionValues] = useState<Record<string, string>>({});

  useEffect(() => {
    // When selectAcross is active, selectedCount can briefly become 0 during table refetch.
    // Do not reset the chosen bulk action in that transient state.
    if (selectedCount === 0 && !selectAcross) {
      setSelectedAction("");
      setOptionValues({});
    }
  }, [selectedCount, selectAcross]);

  const currentAction = actions.find((a) => a.name === selectedAction);
  const currentOptions = useMemo(() => currentAction?.options ?? [], [currentAction]);

  const actionOptions: SelectOption[] = useMemo(
    () => actions.map((a) => ({ value: a.name, label: a.description })),
    [actions],
  );

  const optionChoices = useMemo(() => {
    const map: Record<string, SelectOption[]> = {};
    for (const opt of currentOptions) {
      if (opt.channel) continue;
      map[opt.name] = (opt.choices ?? []).map((c) => ({
        value: c.value,
        label: c.label,
      }));
    }
    return map;
  }, [currentOptions]);

  // Keep the bulk panel mounted while "select all queryset" is active, even if counts/rows are
  // momentarily empty during refetch or query transitions.
  if (actions.length === 0 || (selectedCount === 0 && !selectAcross)) return null;

  const handleActionChange = (value: string) => {
    if (value !== selectedAction) {
      setOptionValues({});
    }
    setSelectedAction(value);
  };

  const handleOptionChange = (name: string, value: string) => {
    setOptionValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    if (!selectedAction || !currentAction) return;
    const handlerName = currentAction.handler ?? "submit_form";
    if (selectedRows.length === 0 && !selectAcross) return;
    if (selectedRows.length === 0 && handlerName === "submit_form") return;
    const handler = getHandler(handlerName);
    handler({
      source: "bulk_action",
      actionName: selectedAction,
      selectedRows,
      optionValues,
      config: currentAction.handler_config,
      tableState,
      selectAcross,
    });
  };

  return (
    <div className="lt-bulk-panel">
      <div className="lt-bulk-panel__row">
        <SelectStatic
          mode="static"
          name="bulk-action"
          options={actionOptions}
          floating
          label="Действия с выбранными объектами"
          initialValue={selectedAction}
          size="medium"
          autoWidth
          menuPlacement="top"
          onChange={handleActionChange}
        />
        {currentOptions.map((opt) => {
          if (opt.channel) {
            return (
              <SelectLookup
                key={`${selectedAction}-${opt.name}`}
                mode="lookup"
                name={`bulk-opt-${opt.name}`}
                lookupUrl={`/ajax_select/ajax_lookup/${opt.channel}`}
                floating
                label={opt.label}
                size="medium"
                autoWidth
                menuPlacement="top"
                onChange={(v) => handleOptionChange(opt.name, v)}
              />
            );
          }
          return (
            <SelectStatic
              key={`${selectedAction}-${opt.name}`}
              mode="static"
              name={`bulk-opt-${opt.name}`}
              options={optionChoices[opt.name] ?? []}
              floating
              label={opt.label}
              initialValue={optionValues[opt.name] ?? ""}
              size="medium"
              autoWidth
              menuPlacement="top"
              onChange={(v) => handleOptionChange(opt.name, v)}
            />
          );
        })}
        <button
          type="button"
          className="rd-btn rd-btn--primary rd-btn--medium"
          onClick={handleSubmit}
          disabled={!selectedAction || currentOptions.some((opt) => opt.required && !optionValues[opt.name])}
        >
          Выполнить
        </button>
        <span className="lt-bulk-panel__count">
          {selectAcross
            ? "Выбраны все объекты"
            : `Выбрано ${selectedCount}${totalCount ? ` из ${totalCount}` : ""}`}
        </span>
      </div>
      <div className="lt-bulk-panel__selection-actions">
        <button type="button" className="lt-bulk-panel__link-btn" onClick={onSelectAll} disabled={selectAcross}>
          Выбрать все
        </button>
        <button type="button" className="lt-bulk-panel__link-btn" onClick={onClearSelection}>
          Снять выделение
        </button>
      </div>
    </div>
  );
}
